import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { publicFormService } from "../services/api";

export const ExternalFormPage = () => {
  const { token } = useParams();

  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState("");
  const [correctionNote, setCorrectionNote] = useState("");

  const [dniInput, setDniInput] = useState("");
  const [dniLoading, setDniLoading] = useState(false);
  const [dniError, setDniError] = useState("");
  const [padronData, setPadronData] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    requestorName: "",
    requestorDni: "",
    notes: "",
    domicilioDeceased: "",
    cuit: "",
    locality: "",
    province: "",
  });

  const [attachments, setAttachments] = useState({
    solicitude1: null,
    solicitude2: null,
    deathCertificate1: null,
    deathCertificate2: null,
    dniDeceased: null,
    dniRequestor: null,
  });

  useEffect(() => {
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const verifyToken = async () => {
    setTokenLoading(true);
    try {
      const res = await publicFormService.getFormByToken(token);
      const s = res.data.submission;
      if (s.correctionNote) setCorrectionNote(s.correctionNote);
      if (s.requestorName)
        setFormData((prev) => ({ ...prev, requestorName: s.requestorName }));
      if (s.requestorDni)
        setFormData((prev) => ({ ...prev, requestorDni: s.requestorDni }));
      if (s.notes) setFormData((prev) => ({ ...prev, notes: s.notes }));
      if (s.cuit) setFormData((prev) => ({ ...prev, cuit: s.cuit }));
      if (s.locality) setFormData((prev) => ({ ...prev, locality: s.locality }));
      if (s.province) setFormData((prev) => ({ ...prev, province: s.province }));
      if (s.dniDeceased && s.namingDeceased) {
        setPadronData({
          dni: s.dniDeceased,
          fullName: s.namingDeceased,
          address: s.domicileDeceased,
        });
        if (s.domicileDeceased)
          setFormData((prev) => ({ ...prev, domicilioDeceased: s.domicileDeceased }));
      }
    } catch (err) {
      setTokenError(
        err.response?.data?.message ||
          "Formulario no encontrado o ya completado.",
      );
    } finally {
      setTokenLoading(false);
    }
  };

  const formatDniWithDots = (val) => {
    const clean = val.replace(/\D/g, "");
    if (clean.length === 8)
      return clean.replace(/(\d{2})(\d{3})(\d{3})/, "$1.$2.$3");
    return val;
  };

  const handleDniSearch = async (e) => {
    e.preventDefault();
    setDniError("");
    setDniLoading(true);
    try {
      const dniWithDots = formatDniWithDots(dniInput);
      const res = await publicFormService.validatePadron(dniWithDots);
      if (res.data.exists) {
        const p = res.data.padron;
        setPadronData({
          dni: dniWithDots,
          fullName: `${p.firstName} ${p.lastName}`,
          address: p.address || "",
        });
        // Pre-llenar domicilio del fallecido desde el padrón
        setFormData((prev) => ({
          ...prev,
          domicilioDeceased: prev.domicilioDeceased || p.address || "",
        }));
      } else {
        setDniError("El DNI ingresado no existe en el padron electoral.");
      }
    } catch {
      setDniError("El DNI ingresado no existe en el padron electoral.");
    } finally {
      setDniLoading(false);
    }
  };

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) setAttachments((prev) => ({ ...prev, [field]: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = [
      "solicitude1",
      "solicitude2",
      "deathCertificate1",
      "deathCertificate2",
      "dniDeceased",
      "dniRequestor",
    ].filter((f) => !attachments[f]);
    if (missing.length > 0) {
      alert(
        "Faltan documentos obligatorios. Por favor adjunta todos los archivos requeridos.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("dniDeceased", padronData.dni);
      fd.append("requestorName", formData.requestorName);
      fd.append("requestorDni", formData.requestorDni);
      fd.append("notes", formData.notes);
      if (formData.domicilioDeceased) fd.append("domicilioDeceased", formData.domicilioDeceased);
      if (formData.cuit) fd.append("cuit", formData.cuit);
      if (formData.locality) fd.append("locality", formData.locality);
      if (formData.province) fd.append("province", formData.province);
      fd.append("attachmentSolicitude1", attachments.solicitude1);
      fd.append("attachmentSolicitude2", attachments.solicitude2);
      fd.append("attachmentDeathCertificate1", attachments.deathCertificate1);
      fd.append("attachmentDeathCertificate2", attachments.deathCertificate2);
      fd.append("attachmentDniDeceased", attachments.dniDeceased);
      fd.append("attachmentDniRequestor", attachments.dniRequestor);
      await publicFormService.submitFormByToken(token, fd);
      setSuccess(true);
    } catch (err) {
      alert("Error al enviar: " + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const FILE_FIELDS = [
    { key: "solicitude1", label: "Solicitud — Documento empresa firmada" },
    { key: "solicitude2", label: "Solicitud 2 — Datos del documento" },
    { key: "deathCertificate1", label: "Certificado de Defunción (Sanatorio)" },
    { key: "deathCertificate2", label: "Certificado de Defunción (Registro Civil)" },
    { key: "dniDeceased", label: "DNI del Fallecido" },
    { key: "dniRequestor", label: "DNI del Solicitante" },
  ];

  if (tokenLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="card p-10 text-center w-full max-w-md">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-slate-500">Verificando formulario...</p>
        </div>
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="card p-8 text-center w-full max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-2">Formulario no disponible</h2>
          <p className="text-slate-600 mb-2">{tokenError}</p>
          <p className="text-sm text-slate-400">Si creés que es un error, contactá al referente que te envió este enlace.</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="card p-8 text-center w-full max-w-md">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-green-700 mb-2">Documentación enviada</h2>
          <p className="text-slate-600">El referente revisará los datos y te contactará si es necesario.</p>
        </div>
      </div>
    );
  }

  const PageShell = ({ children }) => (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="mx-auto w-full max-w-2xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Solicitud de Sepelio</h1>
          <p className="text-sm text-slate-500 mt-1">{padronData ? "Paso 2 de 2 — Completar datos y adjuntar documentos" : "Paso 1 de 2 — Identificar al fallecido"}</p>
        </div>
        {correctionNote && (
          <div className="mb-5 rounded-md bg-amber-50 border border-amber-300 px-4 py-3 text-sm text-amber-800">
            <strong>El referente solicitó correcciones:</strong>
            <p className="mt-1">{correctionNote}</p>
          </div>
        )}
        {children}
      </div>
    </div>
  );

  if (!padronData) {
    return (
      <PageShell>
        <div className="card p-6">
          <h2 className="text-base font-semibold text-slate-700 mb-1">Datos del Fallecido</h2>
          <p className="text-sm text-slate-500 mb-4">
            Ingresá el DNI del fallecido para verificar que figura en el padrón electoral.
          </p>
          <form onSubmit={handleDniSearch} className="space-y-4">
            <div>
              <label htmlFor="dniInput" className="label">DNI del Fallecido *</label>
              <input
                id="dniInput"
                type="text"
                value={dniInput}
                onChange={(e) => setDniInput(e.target.value)}
                placeholder="Ej: 12345678 o 12.345.678"
                required
                className="input max-w-xs"
              />
            </div>
            {dniError && (
              <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{dniError}</div>
            )}
            <button type="submit" disabled={dniLoading} className="btn-primary">
              {dniLoading ? "Verificando..." : "Verificar DNI en el Padrón"}
            </button>
          </form>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* Datos verificados del fallecido */}
      <div className="card p-6 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-700">Fallecido (verificado en el padrón)</h2>
          <button type="button" onClick={() => setPadronData(null)} className="text-xs text-primary-700 underline hover:text-primary-900">
            Cambiar DNI
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
          <div>
            <p className="label">DNI</p>
            <p className="font-medium text-slate-800">{padronData.dni}</p>
          </div>
          <div>
            <p className="label">Nombre y Apellido</p>
            <p className="font-medium text-slate-800">{padronData.fullName}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label">Domicilio del Fallecido</label>
            <input type="text" name="domicilioDeceased" value={formData.domicilioDeceased} onChange={handleChange} className="input" placeholder="Domicilio" />
          </div>
          <div>
            <label className="label">CUIT</label>
            <input type="text" name="cuit" value={formData.cuit} onChange={handleChange} className="input" placeholder="Ej: 20-12345678-9" />
          </div>
          <div>
            <label className="label">Localidad</label>
            <input type="text" name="locality" value={formData.locality} onChange={handleChange} className="input" placeholder="Localidad" />
          </div>
          <div>
            <label className="label">Provincia</label>
            <input type="text" name="province" value={formData.province} onChange={handleChange} className="input" placeholder="Provincia" />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Datos solicitante */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-slate-700 mb-4">Datos del Solicitante (Familiar)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre completo *</label>
              <input type="text" name="requestorName" value={formData.requestorName} onChange={handleChange} required placeholder="Nombre y Apellido" className="input" />
            </div>
            <div>
              <label className="label">DNI del solicitante *</label>
              <input type="text" name="requestorDni" value={formData.requestorDni} onChange={handleChange} required placeholder="Ej: 30.123.456" className="input" />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Notas adicionales</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} rows={3} placeholder="Información adicional relevante..." className="input" />
            </div>
          </div>
        </div>

        {/* Documentos */}
        <div className="card p-6">
          <h2 className="text-base font-semibold text-slate-700 mb-4">Documentos Requeridos (6 archivos obligatorios)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FILE_FIELDS.map(({ key, label }) => (
              <div key={key} className="rounded-lg border border-slate-200 p-3">
                <p className="text-sm font-semibold text-slate-700 mb-1">{label} *</p>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={(e) => handleFileChange(e, key)}
                  className="text-xs text-slate-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {attachments[key] ? (
                  <p className="mt-1 text-xs text-green-600 font-medium">Seleccionado: {attachments[key].name}</p>
                ) : (
                  <p className="mt-1 text-xs text-red-500">Obligatorio</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <button type="submit" disabled={submitting} className="btn-primary w-full justify-center py-3 text-base">
            {submitting ? "Enviando documentación..." : "Enviar Documentación"}
          </button>
        </div>
      </form>
    </PageShell>
  );
};
